// src/components/TerritoryMapEditor.jsx
//
// Simple polygon drawing tool for defining territory geometry on a
// Leaflet map. No external drawing plugin — we keep a local array of
// vertices in React state and render the polygon with react-leaflet
// primitives (Polygon + CircleMarker for handles).
//
// Interaction model:
//   • Click anywhere on the map to append a vertex.
//   • Click an existing vertex handle to remove it.
//   • Click the first vertex handle (or hit the Close button) to close
//     the polygon. Closing emits GeoJSON up to the parent.
//   • The parent receives a GeoJSON Polygon in { type, coordinates }
//     form, using [lng, lat] order per the GeoJSON spec.
//
// Designed to read the initial value as GeoJSON so that editing an
// existing territory loads its previously-saved shape onto the map.

import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, CircleMarker, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Undo2, Trash2, MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Haversine distance in miles between two [lat, lng] points.
function haversineMiles(a, b) {
  const R = 3959;
  const dLat = (b[0] - a[0]) * Math.PI / 180;
  const dLng = (b[1] - a[1]) * Math.PI / 180;
  const lat1 = a[0] * Math.PI / 180;
  const lat2 = b[0] * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// Spherical-excess area in square miles for a closed lat/lng polygon.
// Reasonable approximation for territory-scale shapes.
function polygonAreaSqMi(verts) {
  if (verts.length < 3) return 0;
  const R = 3959;
  let total = 0;
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    total += (b[1] - a[1]) * Math.PI / 180 * (2 + Math.sin(a[0] * Math.PI / 180) + Math.sin(b[0] * Math.PI / 180));
  }
  return Math.abs(total * R * R / 2);
}

// Build a Leaflet divIcon with formatted distance text. Memoized via key so
// React-Leaflet doesn't recreate on every render.
function makeDistanceIcon(label) {
  return L.divIcon({
    className: 'territory-edge-label',
    html: `<div style="
      background: rgba(255,255,255,0.95);
      color: #0F172A;
      font: 600 11px/1 -apple-system, system-ui, sans-serif;
      padding: 2px 6px;
      border-radius: 4px;
      border: 1px solid #E5E9F0;
      box-shadow: 0 1px 2px rgba(15,23,42,0.08);
      white-space: nowrap;
      transform: translate(-50%, -50%);
    ">${label}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

// GeoJSON Polygon → array of [lat, lng] for Leaflet (inverse of what we emit).
function geojsonToLatLngs(geo) {
  if (!geo || geo.type !== 'Polygon' || !Array.isArray(geo.coordinates) || !geo.coordinates[0]) return [];
  const ring = geo.coordinates[0];
  // GeoJSON rings are closed (first === last); drop the duplicate for editing.
  const trimmed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
    ? ring.slice(0, -1)
    : ring;
  return trimmed.map(([lng, lat]) => [lat, lng]);
}

// Array of [lat, lng] → closed GeoJSON Polygon.
function latLngsToGeojson(verts) {
  if (!verts || verts.length < 3) return null;
  const ring = verts.map(([lat, lng]) => [lng, lat]);
  ring.push(ring[0]);
  return { type: 'Polygon', coordinates: [ring] };
}

function ClickCatcher({ onClick }) {
  useMapEvents({ click: (e) => onClick([e.latlng.lat, e.latlng.lng]) });
  return null;
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 2) return;
    const bounds = points.reduce((acc, p) => { acc.push(p); return acc; }, []);
    try { map.fitBounds(bounds, { padding: [40, 40] }); } catch {}
  }, [points, map]);
  return null;
}

export default function TerritoryMapEditor({ value, onChange, color = '#1A3665', referencePoints = [] }) {
  // Local vertex state (array of [lat, lng]). Initialized from the incoming
  // GeoJSON so edits of existing territories load correctly.
  const [vertices, setVertices] = useState(() => geojsonToLatLngs(value));

  // Emit GeoJSON upward whenever the polygon changes. Only emits a value
  // when there are ≥ 3 vertices; otherwise emits null so the parent knows
  // the geometry isn't valid yet.
  useEffect(() => {
    onChange?.(latLngsToGeojson(vertices));
  }, [vertices]); // eslint-disable-line react-hooks/exhaustive-deps

  const addVertex = ([lat, lng]) => setVertices(v => [...v, [lat, lng]]);
  const removeVertex = (idx) => setVertices(v => v.filter((_, i) => i !== idx));
  const undoLast = () => setVertices(v => v.slice(0, -1));
  const clearAll = () => setVertices([]);

  // Edge midpoints + distances for the on-map labels. Includes the closing
  // edge once polygon has 3+ vertices. Renders sized labels at each midpoint.
  const edges = useMemo(() => {
    if (vertices.length < 2) return [];
    const out = [];
    const closed = vertices.length >= 3;
    const n = vertices.length;
    for (let i = 0; i < (closed ? n : n - 1); i++) {
      const a = vertices[i];
      const b = vertices[(i + 1) % n];
      const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const miles = haversineMiles(a, b);
      out.push({ mid, miles, key: `${i}-${a[0].toFixed(4)}-${b[0].toFixed(4)}` });
    }
    return out;
  }, [vertices]);

  const perimeterMiles = edges.reduce((s, e) => s + e.miles, 0);
  const areaSqMi = polygonAreaSqMi(vertices);

  // Center on reference points (e.g. LFG locations) if provided, else US.
  const initialCenter = useMemo(() => {
    if (vertices.length) return vertices[0];
    if (referencePoints.length) {
      const avgLat = referencePoints.reduce((s, p) => s + p.lat, 0) / referencePoints.length;
      const avgLng = referencePoints.reduce((s, p) => s + p.lng, 0) / referencePoints.length;
      return [avgLat, avgLng];
    }
    return [39.8283, -98.5795]; // US center
  }, []); // only on mount
  const initialZoom = referencePoints.length > 0 || vertices.length > 0 ? 6 : 4;

  // Points to fit bounds to — prefer vertices if any, else reference points.
  const fitPoints = vertices.length >= 2
    ? vertices
    : referencePoints.length >= 2
      ? referencePoints.map(p => [p.lat, p.lng])
      : null;

  return (
    <div className="relative rounded-lg overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
      {/* Toolbar */}
      <div
        className="absolute top-2 left-2 z-[500] rounded-lg flex items-center gap-1 px-1 py-0.5"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(6px)',
          border: '1px solid #E5E9F0',
          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
        }}
      >
        <button
          type="button"
          onClick={undoLast}
          disabled={vertices.length === 0}
          className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ color: '#475569' }}
          title="Undo last point"
        >
          <Undo2 className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={vertices.length === 0}
          className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ color: '#DC2626' }}
          title="Clear polygon"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <div className="h-5 w-px" style={{ backgroundColor: '#E5E9F0' }} />
        <span className="text-[11px] font-semibold tabular-nums px-2" style={{ color: '#475569' }}>
          {vertices.length} point{vertices.length === 1 ? '' : 's'}
          {perimeterMiles > 0 && (
            <span style={{ color: '#94A3B8', fontWeight: 500 }}>
              {' · '}{perimeterMiles.toFixed(0)} mi perim
              {areaSqMi > 0 && <>{' · '}{areaSqMi >= 1000 ? `${(areaSqMi / 1000).toFixed(1)}k` : areaSqMi.toFixed(0)} sq mi</>}
            </span>
          )}
        </span>
      </div>

      {/* Status / helper message */}
      {vertices.length < 3 && (
        <div
          className="absolute bottom-2 left-2 right-2 z-[500] rounded-lg px-3 py-2 text-[12px] flex items-start gap-2"
          style={{
            backgroundColor: 'rgba(26, 54, 101, 0.92)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.2)',
          }}
        >
          <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            Click on the map to drop polygon vertices. You need at least 3 points.
            {vertices.length === 1 && ' 2 more to go.'}
            {vertices.length === 2 && ' 1 more to go.'}
          </span>
        </div>
      )}

      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: '400px', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <ClickCatcher onClick={addVertex} />
        {fitPoints && <FitBounds points={fitPoints} />}

        {/* Reference markers (LFG locations etc.) — subtle dots to help orient */}
        {referencePoints.map((p, i) => (
          <CircleMarker
            key={`ref-${i}`}
            center={[p.lat, p.lng]}
            radius={4}
            pathOptions={{ color: '#64748B', fillColor: '#94A3B8', fillOpacity: 0.6, weight: 1 }}
            interactive={false}
          />
        ))}

        {/* Polygon preview (≥ 3 vertices) */}
        {vertices.length >= 3 && (
          <Polygon
            positions={vertices}
            pathOptions={{
              color,
              fillColor: color,
              fillOpacity: 0.18,
              weight: 2,
            }}
          />
        )}

        {/* Line for < 3 points (no closed polygon yet) */}
        {vertices.length === 2 && (
          <Polygon
            positions={vertices}
            pathOptions={{
              color,
              fillOpacity: 0,
              weight: 2,
              dashArray: '4 4',
            }}
          />
        )}

        {/* Edge distance labels (placed at midpoint of each segment). */}
        {edges.map(e => (
          <Marker
            key={`edge-${e.key}`}
            position={e.mid}
            icon={makeDistanceIcon(`${e.miles.toFixed(1)} mi`)}
            interactive={false}
          />
        ))}

        {/* Vertex handles — click to remove, click the FIRST to complete. */}
        {vertices.map((v, i) => (
          <CircleMarker
            key={i}
            center={v}
            radius={6}
            pathOptions={{
              color: 'white',
              fillColor: color,
              fillOpacity: 1,
              weight: 2,
            }}
            eventHandlers={{ click: () => removeVertex(i) }}
          />
        ))}
      </MapContainer>
    </div>
  );
}

// Standard ray-casting point-in-polygon. Accepts (lat, lng) and a GeoJSON
// Polygon (coordinates in [lng, lat] order). Exported so PortfolioView's
// filter can use it.
export function pointInPolygon(lat, lng, polygon) {
  if (!polygon || polygon.type !== 'Polygon' || !polygon.coordinates?.[0]) return false;
  const ring = polygon.coordinates[0];
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect = ((yi > lat) !== (yj > lat)) &&
                      (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
